import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import http from '../api/http';
import {
  ALL_FILIALES_ID,
  canSwitchFiliales,
  getAssignedFiliales,
  getDefaultFilialeId,
  getFilialeById,
  isFilialeAllowed,
  getCountryFlag,
} from '../data/filiales';

const FilialeContext = createContext(null);

const scopeStorageKey = (userId) => `rh_filiale_scope_user_${userId}`;

/**
 * Détermine le scope (filiale sélectionnée) à utiliser pour un utilisateur donné.
 * - Si l'utilisateur peut changer de filiale, on tente de relire son dernier choix
 *   persistant (scopeStorageKey), validé contre la liste réelle des filiales.
 * - Sinon, on retombe sur la filiale par défaut de l'utilisateur.
 *
 * IMPORTANT : cette fonction ne doit être considérée "définitive" que lorsqu'elle
 * est appelée avec les VRAIES filiales (issues de l'API), pas un tableau vide.
 */
const resolveScope = (user, dynamicFiliales) => {
  if (!user) return null;
  const fallback = getDefaultFilialeId(user);
  const role = (user.libprofil || user.role || '').toLowerCase();
  console.log('[resolveScope] fallback:', fallback, 'user.role:', user.role, 'user.libprofil:', user.libprofil, 'detected role:', role);
  if (canSwitchFiliales(user)) {
    const stored = sessionStorage.getItem(scopeStorageKey(user.id));
    console.log('[resolveScope] stored:', stored, 'isFilialeAllowed:', stored ? isFilialeAllowed(user, stored, dynamicFiliales) : false);
    if (stored && isFilialeAllowed(user, stored, dynamicFiliales)) return stored;
  }
  console.log('[resolveScope] returning fallback:', fallback);
  return fallback;
};

export const FilialeProvider = ({ children }) => {
  const { user } = useAuth();
  const [filialeId, setFilialeId] = useState(null);
  const [dynamicFiliales, setDynamicFiliales] = useState([]);
  const [filialesLoading, setFilialesLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setFilialeId(null);
      setDynamicFiliales([]);
      setFilialesLoading(false);
      sessionStorage.removeItem('rh_filiale_scope');
      return;
    }

    setFilialesLoading(true);

    // Charger les filiales depuis l'API
    const fetchFiliales = async () => {
      try {
        const response = await http.get('/api/filiales');
        console.log('[FilialeContext] Filiales chargées:', response.data);
        console.log('[FilialeContext] User role:', user.role, 'libprofil:', user.libprofil);
        console.log('[FilialeContext] User filiales:', user.filiales);

        if (response.data && Array.isArray(response.data)) {
          setDynamicFiliales(response.data);

          // Définir la filiale initiale en utilisant resolveScope avec les filiales chargées
          const initialScope = resolveScope(user, response.data);
          console.log('[FilialeContext] Initial scope:', initialScope);
          if (initialScope) {
            setFilialeId(initialScope);
          } else {
            console.warn('[FilialeContext] Aucun scope initial défini');
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des filiales:', error);
      } finally {
        setFilialesLoading(false);
      }
    };

    fetchFiliales();
  }, [user]);

  const selectFiliale = (id) => {
    if (!user) return;
    const next = String(id);
    setFilialeId(next);
    sessionStorage.setItem(scopeStorageKey(user.id), next);
  };

  const value = useMemo(() => {
    const canSwitch = canSwitchFiliales(user);
    const assigned = getAssignedFiliales(user, dynamicFiliales);
    const role = (user?.role || user?.libprofil || '').toLowerCase();
    // Pour les rôles qui peuvent switcher, utiliser toutes les filiales disponibles
    const switcherRoles = ['super_admin', 'admin_pays', 'admin', 'president', 'directeur', 'manager', 'comptable', 'rh', 'wfm', 'audit', 'visiteur', 'communication', 'responsable intérim'];
    const filialesToUse = switcherRoles.includes(role) ? dynamicFiliales : assigned;
    const isConsolidated = filialeId === ALL_FILIALES_ID;
    const current = isConsolidated
      ? null
      : getFilialeById(filialeId, dynamicFiliales) ||
        dynamicFiliales.find((f) => String(f.id) === String(filialeId)) ||
        null;

    return {
      filialeId,
      currentFiliale: current,
      isConsolidated,
      canSwitch,
      assignedFiliales: filialesToUse || [],
      filialesLoading,
      selectFiliale,
      getCountryFlag,
      scopeLabel: isConsolidated
        ? 'Toutes les filiales'
        : current
          ? `${current.code} · ${current.libelle}`
          : 'Filiale non définie',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filialeId, dynamicFiliales, filialesLoading]);

  return <FilialeContext.Provider value={value}>{children}</FilialeContext.Provider>;
};

export const useFiliale = () => {
  const context = useContext(FilialeContext);
  if (!context) {
    throw new Error('useFiliale must be used within a FilialeProvider');
  }
  return context;
};